import Swiper from 'swiper';
import {
    Navigation, Pagination, A11y, EffectFade, FreeMode, Autoplay, Thumbs
}             from 'swiper/modules';

/**
 * Create a slider using Swiper library
 *
 * @param {HTMLElement} el - The element that contains the slider
 * @param {Object} config - Configuration options for the slider
 * @returns {Swiper|false} - The created Swiper instance
 */
function sliders(el, config = {}) {
    // Check if the element exists
    if (!el) {
        return false;
    }

    // Get the required elements for the slider
    const slider     = el.querySelector('.swiper');
    const next       = el.querySelector('.swiper-button-next');
    const prev       = el.querySelector('.swiper-button-prev');
    const pagination = el.querySelector('.swiper-pagination');

    // Set the default settings for the slider
    const settings = {
        modules               : [Navigation, Pagination, A11y, EffectFade, FreeMode, Autoplay, Thumbs],
        loop                  : false,
        slidesPerView         : 'auto',
        spaceBetween          : 0,
        watchSlidesVisibility : true,
        watchOverflow         : true,
        threshold             : 10,
        observer              : true,
        navigation            : {
            nextEl : next,
            prevEl : prev
        },
        a11y : {
            prevSlideMessage        : 'Предыдущий слайд',
            nextSlideMessage        : 'Следующий слайд',
            paginationBulletMessage : 'Перейти к {{index}}',
            firstSlideMessage       : 'Первый слайд'
        },
        pagination : {
            el        : pagination,
            type      : 'bullets',
            clickable : true
        },
        ...config
    };

    // Create and return a new Swiper instance
    return new Swiper(slider, settings);
}

/**
 * Create a responsive slider that initializes/destroys based on breakpoint
 *
 * @param {HTMLElement} el - The element that contains the slider
 * @param {Object} config - Configuration options for the slider
 * @param {number} [config.breakpoint=681] - Breakpoint in pixels (slider disabled above this width)
 * @param {Object} [config.settings={}] - Swiper settings to merge with defaults
 * @returns {Object|false} - Object with swiper instance and destroy method, or false if element not found
 */
function slidersResize(el, config = {}) {
    if (!el) {
        return false;
    }

    // Extract breakpoint from config, default to 681px
    const breakpointValue = config.breakpoint ?? 681;
    const userSettings    = config.settings ?? {};

    // Get the required elements for the slider
    const slider     = el.querySelector('.swiper');
    const pagination = el.querySelector('.swiper-pagination');

    if (!slider) {
        console.warn('Slider element (.swiper) not found');
        return false;
    }

    // Create media query with custom breakpoint
    const breakpoint = window.matchMedia(`(min-width: ${breakpointValue}px)`);

    let swiper;
    let mediaQueryListener;

    // Default settings for the slider
    const settings = {
        loop                  : false,
        slidesPerView         : 1,
        spaceBetween          : 16,
        preloadImages         : false,
        watchSlidesVisibility : true,
        watchOverflow         : true,
        threshold             : 0,
        observer              : true,
        pagination            : {
            el             : pagination,
            type           : 'bullets',
            clickable      : true,
            dynamicBullets : false
        },
        ...userSettings
    };

    /**
     * Initialize the Swiper slider
     */
    const enableSlider = () => {
        if (!swiper) {
            swiper = new Swiper(slider, settings);
        }
    };

    /**
     * Destroy the Swiper slider
     */
    const disableSlider = () => {
        if (swiper) {
            swiper.destroy(true, true);
            swiper = undefined;
        }
    };

    /**
     * Check breakpoint and enable/disable slider accordingly
     */
    const breakpointChecker = () => {
        if (breakpoint.matches) {
            // Above breakpoint - disable slider
            disableSlider();
        } else {
            // Below breakpoint - enable slider
            enableSlider();
        }
    };

    /**
     * Cleanup function to remove event listeners
     */
    const cleanup = () => {
        disableSlider();
        if (mediaQueryListener) {
            try {
                breakpoint.removeEventListener('change', mediaQueryListener);
            } catch (e) {
                // Safari <14
                breakpoint.removeListener(mediaQueryListener);
            }
        }
    };

    // Set up event listener with fallback for older browsers
    mediaQueryListener = breakpointChecker;
    try {
        breakpoint.addEventListener('change', mediaQueryListener);
    } catch (e) {
        // Safari <14
        breakpoint.addListener(mediaQueryListener);
    }

    // Initial check
    breakpointChecker();

    // Return object with cleanup method for manual destruction
    return {
        swiper : () => swiper,
        cleanup
    };
}

function slidersHero(el, config = {}) {
    // Check if the element exists
    if (!el) {
        return false;
    }

    // Get the required elements for the slider
    const slider      = el.querySelector('.swiper');
    const next        = el.querySelector('.swiper-button-next');
    const prev        = el.querySelector('.swiper-button-prev');
    const progressBar = el.querySelector('[data-slider="autoplay"]');
    const pagination  = el.querySelector('.swiper-pagination');

    // Set the default settings for the slider
    const autoplayDelay = 5000;

    const settings = {
        modules      : [Pagination, A11y, EffectFade, Autoplay],
        spaceBetween : 10,
        effect       : 'fade',
        fadeEffect   : {
            crossFade : true
        },
        autoplay : {
            delay                : autoplayDelay,
            disableOnInteraction : false,
            pauseOnMouseEnter    : false,
            waitForTransition    : false
        },
        pagination : {
            el        : '.swiper-pagination',
            clickable : true,
            renderBullet(index, className) {
                return `<span class="${className}">` + `0${index + 1}</span>`;
            }
        },
        on : {
            init() {
                animateProgressBar(autoplayDelay);
            },
            slideChange() {
                animateProgressBar(autoplayDelay);
            }
        }
    };

    function animateProgressBar(duration) {
        if (!progressBar) return;

        progressBar.style.transition = 'none';
        progressBar.style.transform  = 'scaleY(0)';

        // принудительный reflow
        progressBar.offsetHeight;

        progressBar.style.transition = `transform ${duration}ms linear`;
        progressBar.style.transform  = 'scaleY(1)';
    }

    // Create and return a new Swiper instance
    return new Swiper(slider, settings);
}

function slidersLine(el, config = {}) {
    // Check if the element exists
    if (!el) {
        return false;
    }

    // Get the required elements for the slider
    const slider = el.querySelector('.swiper');

    // Set the default settings for the slider
    const settings = {
        modules       : [Autoplay],
        slidesPerView : 'auto',
        spaceBetween  : 0,
        loop          : true,
        autoplay      : {
            delay                : 0,
            disableOnInteraction : false,
            pauseOnMouseEnter    : false
        },
        speed : 4500
    };

    // Create and return a new Swiper instance
    return new Swiper(slider, settings);
}

function slidersThumb(el, config = {}) {
    // Check if the element exists
    if (!el) {
        return false;
    }

    // Get the required elements for the sliders
    const swiperThumb = el.querySelector('[data-slider="thumbs"]');
    const swiperMain  = el.querySelector('[data-slider="main"]');

    if (!swiperThumb || !swiperMain) {
        return false;
    }

    // Set the default settings for the thumbnail slider
    const thumbSettings = {
        modules             : [FreeMode, Thumbs],
        spaceBetween        : 8,
        slidesPerView       : 4,
        freeMode            : true,
        watchSlidesProgress : true,
        ...config.thumb
    };

    // Create thumbnail swiper instance
    const thumbSwiper = new Swiper(swiperThumb, thumbSettings);

    // Set the default settings for the main slider
    const mainSettings = {
        modules       : [Thumbs],
        spaceBetween  : 10,
        slidesPerView : 1,
        thumbs        : {
            swiper : thumbSwiper
        },
        ...config.main
    };

    // Create and return main swiper instance
    return new Swiper(swiperMain, mainSettings);
}

function sliderDis(el, config = {}) {
    if (!el) {
        return false;
    }

    const breakpointValue = config?.breakpoint || '767px';
    const breakpoint      = window.matchMedia(`(min-width: ${breakpointValue})`);
    const slider          = el.querySelector('.swiper');
    const pagination      = el.querySelector('.swiper-pagination');
    let swiper;

    const settings = {
        modules        : [Navigation, Pagination],
        loop           : false,
        slidesPerView  : 'auto',
        threshold      : 0,
        observer       : true,
        observeParents : true,
        pagination     : {
            el   : pagination,
            type : 'progressbar'
        }
    };

    if (config) {
        const {
            breakpoint:_,
            ...swiperConfig
        } = config;
        Object.assign(settings, swiperConfig);
    }

    const enableSlider = () => {
        swiper = new Swiper(slider, settings);
    };

    const breakpointChecker = () => {
        if (breakpoint.matches) {
            if (swiper) {
                swiper.destroy(true, true);
                swiper = undefined;
            }
        } else if (!swiper) {
            enableSlider();
        }
    };

    try {
        breakpoint.addEventListener('change', breakpointChecker);
    } catch (e) {
        breakpoint.addListener(breakpointChecker);
    }

    breakpointChecker();

    return swiper;
}

export default {
    sliders,
    slidersResize,
    slidersHero,
    slidersLine,
    slidersThumb,
    sliderDis  //
};
